'use client'

import React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../utils/styles'

// Define the core badge styles
const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

// Status-specific variants with semantic colors
const statusVariants = cva('border transition-colors', {
  variants: {
    variant: {
      success:
        'bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800',
      warning:
        'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950 dark:text-yellow-400 dark:border-yellow-800',
      error:
        'bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-400 dark:border-red-800',
      info:
        'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-400 dark:border-blue-800',
      neutral:
        'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-950 dark:text-gray-400 dark:border-gray-800',
      pending:
        'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950 dark:text-yellow-400 dark:border-yellow-800',
      completed:
        'bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800',
      declined:
        'bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-400 dark:border-red-800',
      accepted:
        'bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800',
      expired:
        'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-950 dark:text-gray-400 dark:border-gray-800',
      dismissed:
        'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-950 dark:text-gray-400 dark:border-gray-800',
    },
  },
  defaultVariants: {
    variant: 'neutral',
  },
})

export interface StatusBadgeProps extends VariantProps<typeof statusVariants> {
  status?: string
  variant?:
    | 'success'
    | 'warning'
    | 'error'
    | 'info'
    | 'neutral'
    | 'pending'
    | 'completed'
    | 'declined'
    | 'accepted'
    | 'expired'
    | 'dismissed'
  children?: React.ReactNode
  className?: string
  showIcon?: boolean
  size?: 'sm' | 'md' | 'lg'
}

// Status to variant mapping for automatic status detection
function mapStatusToVariant(
  status: string,
): StatusBadgeProps['variant'] {
  const mapping: Record<string, StatusBadgeProps['variant']> = {
    // Participation statuses
    pending: 'pending',
    accepted: 'accepted',
    declined: 'declined',
    expired: 'expired',

    // Task/Process statuses
    completed: 'completed',
    in_progress: 'info',
    not_started: 'neutral',

    // Alert statuses
    sent: 'info',
    acknowledged: 'success',
    dismissed: 'dismissed',

    // Performance statuses
    on_track: 'success',
    at_risk: 'warning',
    delayed: 'error',
    confirmed: 'success',

    // Staffing statuses
    fully_staffed: 'success',
    understaffed: 'warning',
    critical: 'error',

    // General statuses
    active: 'success',
    inactive: 'neutral',
    draft: 'neutral',
    published: 'success',
    archived: 'dismissed',
  }

  return mapping[status.toLowerCase()] || 'neutral'
}

// Status icons mapping
function getStatusIcon(status?: string, variant?: StatusBadgeProps['variant']): string | null {
  const iconMapping: Record<string, string> = {
    success: '✓',
    completed: '✓',
    accepted: '✓',
    confirmed: '✓',
    acknowledged: '✓',

    pending: '⏳',
    in_progress: '⏳',

    warning: '⚠',
    at_risk: '⚠',
    understaffed: '⚠',

    error: '✕',
    declined: '✕',
    delayed: '✕',
    critical: '✕',

    info: 'ℹ',
    sent: 'ℹ',

    neutral: '○',
    not_started: '○',
    draft: '○',
    dismissed: '○',
    expired: '○',
    archived: '○',
  }

  if (status && iconMapping[status.toLowerCase()]) {
    return iconMapping[status.toLowerCase()]
  }

  if (variant && iconMapping[variant]) {
    return iconMapping[variant]
  }

  return null
}

export function StatusBadge({
  status,
  variant,
  children,
  className,
  showIcon = false,
  size = 'md',
  ...props
}: StatusBadgeProps) {
  const mappedVariant = variant || (status ? mapStatusToVariant(status) : 'neutral')
  const icon = showIcon ? getStatusIcon(status, mappedVariant) : null

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-0.5 text-xs',
    lg: 'px-3 py-1 text-sm',
  }

  const displayText = children || status

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        sizeClasses[size],
        statusVariants({ variant: mappedVariant }),
        className
      )}
      {...props}
    >
      {icon && <span className="mr-1" aria-hidden="true">{icon}</span>}
      {displayText}
    </span>
  )
}

export default StatusBadge